import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { get } from 'lodash'
import { useSelector } from 'react-redux'
import notify from 'components/atoms/Notify'
import { useTranslation } from 'react-i18next'
import useModal from 'hooks/useModal'
import apiPost from 'lib/network/apiPost'

import { isCompanyDashboardUser, isPartnerDashboardUser } from 'lib/auth/dashboardRole'
import BookingRefunds from './BookingRefunds'
import BookingDeliveryCard from './components/BookingDeliveryCard'
import BookingDetailsCard from './components/BookingDetailsCard'
import BookingOrderSummaryCard from './components/BookingOrderSummaryCard'
import BookingCompanyPaymentCard from './components/BookingCompanyPaymentCard'
import BookingAjarleePaymentCard from './components/BookingAjarleePaymentCard'
import BookingCarDetailsCard from './components/BookingCarDetailsCard'
import BookingUserDetailsCard from './components/BookingUserDetailsCard'
import BookingPartnerDetailsCard from './components/BookingPartnerDetailsCard'
import BookingCompanyAlertsCard from './components/BookingCompanyAlertsCard'
import BookingCompanyBranchCard from './components/BookingCompanyBranchCard'
import BookingAcknowledgmentCard from './components/BookingAcknowledgmentCard'

const Booking = ({ loading, bookingData, isEdit, id, refetch, streamConnected }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [orderStatus, setOrderStatus] = useState(bookingData?.status)
  const history = useHistory()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  const isPartnerOnly = isPartnerDashboardUser(userFromStore)

  const previousStatus = useRef(bookingData?.status)

  const [previewDocument, setPreviewDocument] = useState({ url: '', title: '', type: '' })
  const [fullscreenImage, setFullscreenImage] = useState({ url: '', title: '' })

  useEffect(() => {
    setOrderStatus(bookingData?.status)

    if (previousStatus.current && previousStatus.current !== bookingData?.status) {
      const statusElement = document.getElementById('booking-status')
      if (statusElement) {
        statusElement.classList.add('animate-pulse')
        setTimeout(() => {
          statusElement.classList.remove('animate-pulse')
        }, 2000)
      }
    }

    previousStatus.current = bookingData?.status
  }, [bookingData])

  const handleViewBooking = bookingId => {
    history.push(`/booking/${bookingId}`)
  }

  const handleCopyBookingId = text => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success(t('booking.messages.copiedToClipboard'))
    })
  }

  const openDocumentPreview = (documentUrl, documentTitle, documentType = 'image') => {
    setPreviewDocument({ url: documentUrl, title: documentTitle, type: documentType })
    openPreviewModal()
  }

  const openFullscreenImage = (imageUrl, imageTitle) => {
    setFullscreenImage({ url: imageUrl, title: imageTitle })
    openFullscreenModal()
  }

  const hasDeliveryDetails = Boolean(
    bookingData?.delivery &&
      (bookingData?.delivery?.type ||
        bookingData?.delivery?.pickupLocationAddress ||
        bookingData?.delivery?.dropOffLocationAddress ||
        bookingData?.delivery?.pickupLocation?.coordinates?.length ||
        bookingData?.delivery?.dropOffLocation?.coordinates?.length)
  )

  const updateDocumentVerification = async (documentType, isVerified) => {
    const userId = bookingData?.user?._id

    if (!userId) {
      notify.error(t('userProfile.messages.userIdNotFound'))
      return
    }

    if (!isVerified) {
      notify.deleteConfirm({
        title: t('userProfile.messages.confirmRejectDocumentTitle', { documentType }),
        message: t('userProfile.messages.confirmRejectDocumentMessage', { documentType }),
        confirmText: t('userProfile.buttons.reject'),
        cancelText: t('userProfile.buttons.cancel'),
        t,
        onConfirm: async () => {
          await processDocumentVerification(userId, documentType, isVerified)
        },
      })
    } else {
      await processDocumentVerification(userId, documentType, isVerified)
    }
  }

  const processDocumentVerification = async (userId, documentType, isVerified) => {
    try {
      const payload = {}

      if (documentType === 'idCard') {
        payload.isIdCardVerified = isVerified
      } else if (documentType === 'drivingLicense') {
        payload.isDrivingLicenseVerified = isVerified
      } else if (documentType === 'passport') {
        payload.isPassportVerified = isVerified
      }

      const response = await apiPost(`verify-upload-documents/${userId}`, payload)

      if (response.success) {
        notify.success(t('userProfile.messages.documentVerificationUpdatedSuccessfully', { documentType }))
        if (refetch) refetch()
      }
    } catch (error) {
      notify.error(t('userProfile.messages.failedToUpdateDocumentVerification', { documentType }))
      // eslint-disable-next-line no-console -- debug server failures
      console.error('Document verification update error:', error)
    }
  }

  const FullscreenImageContent = () => {
    const handleImageClick = () => {
      closeFullscreenModal()
    }

    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <div onClick={handleImageClick} role="button" tabIndex={0}>
          <img
            src={fullscreenImage.url}
            alt={fullscreenImage.title}
            className="max-w-full max-h-full object-contain cursor-zoom-out"
            onError={e => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
        </div>

        <div style={{ display: 'none' }} className="text-center text-gray-500 p-8">
          <p>{t('userProfile.modal.imageNotAvailable')}</p>
        </div>
      </div>
    )
  }

  const [FullscreenModal, openFullscreenModal, closeFullscreenModal] = useModal({
    content: <FullscreenImageContent />,
    title: fullscreenImage.title,
    dimensions: {
      height: '95vh',
      width: '95vw',
    },
    closeable: true,
  })

  const [PreviewModal, openPreviewModal] = useModal({
    content: (
      <div className="flex flex-col items-center justify-center">
        {previewDocument.type === 'pdf' ? (
          <embed src={previewDocument.url} width="100%" height="500px" type="application/pdf" className="border rounded" />
        ) : (
          <img
            src={previewDocument.url}
            alt={previewDocument.title}
            className="max-w-full max-h-[70vh] object-contain"
            onError={e => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
        )}
        <div style={{ display: 'none' }} className="text-center text-gray-500 p-8">
          <p>{t('userProfile.modal.previewNotAvailable')}</p>
          <p className="text-sm mt-2">{t('userProfile.modal.downloadFileToView')}</p>
        </div>
      </div>
    ),
    title: t('userProfile.modal.previewDocument', { title: previewDocument.title }),
    dimensions: {
      height: '80vh',
      width: '80vw',
      maxWidth: '900px',
    },
    closeable: true,
  })

  return (
    <div className="flex flex-col pb-24 w-full">
      <div className="relative flex w-full flex-col bg-slate-50/70 px-3 py-5 sm:px-6 sm:py-6 lg:px-8">
        <div className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-col items-start gap-4 mb-5">
          {streamConnected && (
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/80 bg-emerald-50/90 px-3 py-1.5 text-xs font-medium text-emerald-800">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              {t('booking.messages.liveUpdatesConnected')}
            </div>
          )}
          {hasAdminAccess && !isCompanyDashboardUser(userFromStore) && !isPartnerOnly && <BookingRefunds summary={bookingData?.refundSummary} />}
        </div>

        <div className="relative z-10 flex flex-col justify-center w-full max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
            <div className="flex w-full flex-col gap-5">
              <div className="contents">
                {hasDeliveryDetails && <BookingDeliveryCard bookingData={bookingData} t={t} />}
                <BookingDetailsCard bookingData={bookingData} t={t} orderStatus={orderStatus} onCopyBookingId={handleCopyBookingId} hasDeliveryDetails={hasDeliveryDetails} />
                <BookingOrderSummaryCard bookingData={bookingData} t={t} isCompanyRole={isCompanyRole} hasAdminAccess={hasAdminAccess} isPartnerOnly={isPartnerOnly} />
                {!isPartnerOnly && <BookingCompanyPaymentCard bookingData={bookingData} t={t} isCompanyRole={isCompanyRole} hasAdminAccess={hasAdminAccess} />}
                {!isPartnerOnly && <BookingAjarleePaymentCard bookingData={bookingData} t={t} isCompanyRole={isCompanyRole} hasAdminAccess={hasAdminAccess} />}
              </div>
            </div>

            <div className="flex w-full flex-col gap-5">
              <BookingCarDetailsCard bookingData={bookingData} t={t} isArabic={isArabic} />
              {bookingData?.user && (
                <BookingUserDetailsCard
                  bookingData={bookingData}
                  t={t}
                  hasAdminAccess={hasAdminAccess}
                  canViewUserDocuments={hasAdminAccess || isCompanyDashboardUser(userFromStore) || (isCompanyRole && !isPartnerOnly)}
                  onUpdateDocumentVerification={updateDocumentVerification}
                  onOpenDocumentPreview={openDocumentPreview}
                  onOpenFullscreenImage={openFullscreenImage}
                />
              )}
              <BookingPartnerDetailsCard bookingData={bookingData} t={t} />
              {!isPartnerOnly && (
                <BookingCompanyAlertsCard bookingData={bookingData} t={t} isCompanyRole={isCompanyRole} hasAdminAccess={hasAdminAccess} onViewBooking={handleViewBooking} />
              )}
              <BookingCompanyBranchCard bookingData={bookingData} t={t} />
              <BookingAcknowledgmentCard bookingData={bookingData} t={t} />
            </div>
          </div>
        </div>
      </div>
      <PreviewModal />
      <FullscreenModal />
    </div>
  )
}

export default Booking
